import { useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { TestTube, ArrowRight, AlertTriangle } from 'lucide-react'
import { TEST_PANELS } from '@/testlab/panels/registry'

export function TestlabPage() {
  const { t } = useTranslation('common')
  const navigate = useNavigate()

  // Auto-navigate to last used panel if available
  useEffect(() => {
    const lastPanel = localStorage.getItem('testlab.lastPanel')
    if (lastPanel && TEST_PANELS.some(panel => panel.path === lastPanel)) {
      // Don't auto-navigate on first load, let user choose
    }
  }, [])

  const handlePanelClick = (panelPath: string) => {
    localStorage.setItem('testlab.lastPanel', panelPath)
    navigate(panelPath)
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Dev Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          <div>
            <p className="font-medium text-amber-800">{t('testlab.banner')}</p>
            <p className="text-sm text-amber-700 mt-1">
              Denna miljö är endast för intern testning och utveckling av analysmoduler.
            </p>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <TestTube className="h-8 w-8 text-blue-600" />
          <h1 className="text-3xl font-bold tracking-tight">{t('testlab.title')}</h1>
        </div>
        <p className="text-lg text-muted-foreground">
          {t('testlab.choosePanel')}
        </p>
      </div>

      {/* Test Panels Grid */}
      <div className="grid gap-6 md:grid-cols-2">
        {TEST_PANELS.map((panel) => (
          <Card key={panel.key} className="cursor-pointer hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{t(panel.titleKey)}</CardTitle>
                <Badge variant="secondary">Modul {panel.key === 'tax-account' ? '6' : '?'}</Badge>
              </div>
              <CardDescription>{t(panel.descriptionKey)}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={() => handlePanelClick(panel.path)}
                className="w-full gap-2"
              >
                Öppna testpanel
                <ArrowRight className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Development Notes */}
      <Card className="border-dashed">
        <CardHeader>
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Utvecklingsanteckningar
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>
            • Paneler testas här innan de integreras i produktionsflöden
          </p>
          <p>
            • Alla findings och analyser körs lokalt utan att påverka kunddatabasen
          </p>
          <p>
            • Använd "Visa JSON" för att inspektera rå analysresultat
          </p>
        </CardContent>
      </Card>
    </div>
  )
}